import { useState } from 'react';
import { ApiError } from '@rydres/api-client';
import { ChatSheet, Notice, fmtPhone, useConversation } from '@rydres/ui';
import { driverChat } from '../lib/api.js';
import { lastLine, unreadChanged, useOpenChat, when } from '../lib/chat.js';
import type { ConversationSummary } from '../lib/types.js';

/** Message and Call: big enough to tap. */
export const CHIP = 'inline-flex min-h-11 items-center rounded-xl bg-card2 px-4 text-sm font-bold disabled:opacity-60';

/** A conversation over the current screen (see `ChatSheet`). */
export function ChatPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const convo = useConversation(id, driverChat, unreadChanged);
  return <ChatSheet key={id} convo={convo} onClose={onClose} />;
}

export function UnreadCount({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="shrink-0 rounded-full bg-danger px-2 py-0.5 text-xs font-black text-card" aria-label={`${n} unread`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * Message and Call for one person in People. Message shows when a message would reach them, or
 * when there is a conversation to read; it opens over the list.
 */
export function ContactButtons({
  name,
  phone,
  conversationId,
  canMessage,
  open,
}: {
  name: string;
  phone: string | null;
  conversationId: string | null;
  canMessage: boolean;
  /** Starts the conversation when there is none yet. */
  open: () => Promise<{ id: string }>;
}) {
  const openChat = useOpenChat();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const message = async () => {
    setBusy(true);
    setError(null);
    try {
      openChat(conversationId ?? (await open()).id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open the conversation.');
    } finally {
      setBusy(false);
    }
  };
  if (!(canMessage || conversationId) && !phone) return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(canMessage || conversationId) && (
          <button type="button" disabled={busy} onClick={() => void message()} className={CHIP} aria-label={`Message ${name}`}>
            Message
          </button>
        )}
        {phone && (
          <a href={`tel:${phone}`} className={CHIP} aria-label={`Call ${name} at ${fmtPhone(phone)}`}>
            Call<span className="hidden md:inline">&nbsp;{fmtPhone(phone)}</span>
          </a>
        )}
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
    </div>
  );
}

/** Conversations whose person is no longer on the list: still there to read until the 12-month clean-up. */
export function EarlierConversations({ list }: { list: ConversationSummary[] }) {
  const openChat = useOpenChat();
  if (!list.length) return null;
  return (
    <section className="space-y-2">
      <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Earlier conversations</h2>
      {list.map((c) => (
        <button key={c.id} type="button" onClick={() => openChat(c.id)} className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-line p-3 text-left">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className={`truncate ${c.unread ? 'font-black' : 'font-bold'}`}>{c.name}</span>
              {c.last && <span className="shrink-0 text-xs text-muted">{when(c.last.at)}</span>}
            </div>
            <div className="truncate text-sm text-ink2">{lastLine(c)}</div>
          </div>
          <UnreadCount n={c.unread} />
        </button>
      ))}
    </section>
  );
}
